import {FC, useState} from 'react'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle,
    ModalXCloseButton
} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {InputText} from '@/components/commons/input-text/InputText'
import {useTranslation} from '@/translations/i18n'
import {WarningLabel} from '@/components/ui/warning-label/WarningLabel'
import {LARGE_SEND_THRESHOLD} from '@/features/mailer/constants'
import {formatBytes} from '@/features/mailer/utils'
import type {MailerLiveCount} from '@/features/mailer/hooks/useMailerLiveCount'

type SendMode = 'test' | 'production'

interface MailerSendConfirmationDialogProps {
    mode: SendMode
    testRecipients: string[]
    onClose: () => void
    onConfirm: () => void
    isSending: boolean
    subject: string
    body: string
    attachments: File[]
    liveCount: MailerLiveCount
    includePatients: boolean
    includeConsultants: boolean
    hasPatientFilters: boolean
    hasConsultantFilters: boolean
}

const CONFIRM_WORD = 'INVIA'

export const MailerSendConfirmationDialog: FC<MailerSendConfirmationDialogProps> = ({
    mode,
    testRecipients,
    onClose,
    onConfirm,
    isSending,
    subject,
    body,
    attachments,
    liveCount,
    includePatients,
    includeConsultants,
    hasPatientFilters,
    hasConsultantFilters
}) => {
    const {t} = useTranslation()
    const [confirmText, setConfirmText] = useState('')

    const isTest = mode === 'test'
    const recipientCount = isTest ? testRecipients.length : liveCount.patients + liveCount.consultants
    const requiresTypeConfirm = !isTest && recipientCount >= LARGE_SEND_THRESHOLD
    const typeConfirmMatches = confirmText.trim().toUpperCase() === CONFIRM_WORD

    const showAllPatientsWarning = !isTest && includePatients && !hasPatientFilters
    const showAllConsultantsWarning = !isTest && includeConsultants && !hasConsultantFilters

    const totalAttachmentsSize = attachments.reduce((sum, f) => sum + f.size, 0)

    const validationReason = (() => {
        if (!subject) return t('mailer:validation_subject_missing')
        if (!body) return t('mailer:validation_body_missing')
        if (!isTest && liveCount.isLoading) return t('mailer:validation_live_count_pending')
        if (!isTest && liveCount.isError) return t('mailer:validation_live_count_error')
        if (!isTest && recipientCount === 0) return t('mailer:validation_no_recipients')
        if (requiresTypeConfirm && !typeConfirmMatches) return t('mailer:send_confirmation_type_to_confirm')
        return null
    })()

    const canConfirm = !validationReason

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="520px">
                <ModalHeader>
                    <Flexbox justify="space-between" align="center" fullWidth>
                        <ModalTitle>
                            {isTest ? t('mailer:test_mode_dialog_title') : t('mailer:send_confirmation_title')}
                        </ModalTitle>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>
                </ModalHeader>
                <ModalBody>
                    <Flexbox direction="column" gap={4} fullWidth>
                        {isTest && (
                            <Text size="textSm" palette="neutral" shade="700">
                                {t('mailer:test_mode_dialog_description')}
                            </Text>
                        )}

                        <Flexbox direction="column" gap={1} fullWidth>
                            <Text size="textXs" weight={600}>
                                {t('mailer:send_confirmation_subject_label')}
                            </Text>
                            <Text size="textSm">{subject || '—'}</Text>
                        </Flexbox>

                        {isTest ? (
                            <Flexbox direction="column" gap={1} fullWidth>
                                <Text size="textXs" weight={600}>
                                    {t('mailer:test_mode_recipients_label')}
                                </Text>
                                {testRecipients.map(email => (
                                    <Text key={email} size="textSm">
                                        • {email}
                                    </Text>
                                ))}
                            </Flexbox>
                        ) : (
                            <Flexbox direction="column" gap={1} fullWidth>
                                <Text size="textXs" weight={600}>
                                    {t('mailer:send_confirmation_recipients_label', {count: recipientCount})}
                                </Text>
                                {includePatients && (
                                    <Text size="textSm">
                                        •{' '}
                                        {t('mailer:send_confirmation_recipients_patients', {
                                            count: liveCount.patients
                                        })}
                                    </Text>
                                )}
                                {includeConsultants && (
                                    <Text size="textSm">
                                        •{' '}
                                        {t('mailer:send_confirmation_recipients_consultants', {
                                            count: liveCount.consultants
                                        })}
                                    </Text>
                                )}
                            </Flexbox>
                        )}

                        {attachments.length > 0 && (
                            <Flexbox direction="column" gap={1} fullWidth>
                                <Text size="textXs" weight={600}>
                                    {t('mailer:send_confirmation_attachments_label', {
                                        count: attachments.length,
                                        size: formatBytes(totalAttachmentsSize)
                                    })}
                                </Text>
                                <Text size="textSm">{attachments.map(f => f.name).join(', ')}</Text>
                            </Flexbox>
                        )}

                        {showAllPatientsWarning && <WarningLabel text={t('mailer:send_all_patients_warning')} />}
                        {showAllConsultantsWarning && <WarningLabel text={t('mailer:send_all_consultants_warning')} />}

                        {requiresTypeConfirm && (
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Text size="textSm">{t('mailer:send_confirmation_type_to_confirm')}</Text>
                                <InputText
                                    type="text"
                                    placeholder={CONFIRM_WORD}
                                    value={confirmText}
                                    onChange={e => setConfirmText(e.target.value)}
                                    disabled={isSending}
                                />
                            </Flexbox>
                        )}
                    </Flexbox>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onClose} type="button" disabled={isSending}>
                        <Text size="textMd">{t('commons:cancel')}</Text>
                    </Button>
                    {validationReason && !isSending ? (
                        <Tooltip content={validationReason} variant="light">
                            <Button variant={isTest ? 'secondary' : 'primaryNeutral'} type="button" disabled>
                                <Text size="textMd">{isTest ? t('mailer:send_test') : t('mailer:send')}</Text>
                            </Button>
                        </Tooltip>
                    ) : (
                        <Button
                            variant={isTest ? 'secondary' : 'primaryNeutral'}
                            onClick={onConfirm}
                            disabled={isSending || !canConfirm}
                            type="button"
                        >
                            <Text size="textMd">
                                {isSending ? t('mailer:sending') : isTest ? t('mailer:send_test') : t('mailer:send')}
                            </Text>
                            {isSending && <Spinner />}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </>
    )
}
